import React from 'react';
import { Link2 } from 'lucide-react';
import { BlocEmail } from './SuiviPrivatisationNotificationBlocks';
import { GroupeNotifications } from '../ui/LigneNotification';

const JETONS_COMMUNS = [
    { token: '{{prenom}}', label: "Prénom de l'organisateur" },
    { token: '{{nom}}', label: "Nom complet de l'organisateur" },
    { token: '{{evenement}}', label: 'Nom du dossier (client ou société)' },
    { token: '{{entreprise}}', label: 'Votre établissement' },
    { token: '{{logo}}', label: 'Votre logo' },
];
const JETONS_ACCES = [...JETONS_COMMUNS,
    { token: '{{date}}', label: "Date de l'événement" },
    { token: '{{code}}', label: 'Le code à 6 chiffres' },
    { token: '{{expire_le}}', label: "Date de fin de validité de l'accès" },
    { token: '{{bouton}}', label: 'Bouton vers le portail' },
];
const JETONS_ENVOI = [...JETONS_COMMUNS,
    { token: '{{nombre}}', label: 'Nombre de courriels envoyés' },
    { token: '{{type_envoi}}', label: '« invitation(s) » ou « relance(s) »' },
    { token: '{{silencieux}}', label: 'Nombre de convives sans réponse' },
    { token: '{{bouton}}', label: 'Bouton vers le portail' },
];
const JETONS_ABSENCE = [
    { token: '{{organisateur}}', label: "Nom de l'organisateur" },
    { token: '{{convive}}', label: 'Nom du convive absent' },
    { token: '{{evenement}}', label: 'Nom du dossier' },
    { token: '{{entreprise}}', label: 'Votre établissement' },
    { token: '{{logo}}', label: 'Votre logo' },
];

export interface OrganisateurNotifications {
    organisateur_acces_subject: string;
    organisateur_acces_body: string;
    notify_organisateur_envoi: boolean;
    organisateur_envoi_subject: string;
    organisateur_envoi_body: string;
    notify_organisateur_absence: boolean;
    organisateur_absence_subject: string;
    organisateur_absence_body: string;
}

export const ORGANISATEUR_NOTIFS_VIDES: OrganisateurNotifications = {
    organisateur_acces_subject: '', organisateur_acces_body: '',
    notify_organisateur_envoi: true, organisateur_envoi_subject: '', organisateur_envoi_body: '',
    notify_organisateur_absence: true, organisateur_absence_subject: '', organisateur_absence_body: '',
};

/**
 * Section « Portail organisateur » de l'onglet Notifications (refonte design lot 10) — les trois courriels
 * du portail. N'apparaît que si le module Menus & Convives est actif. Enregistrée à part, par section
 * (`PATCH /client-notification-settings/organisateur`), comme le veut le pipeline depuis 2026-08.
 *
 * Le courriel d'ACCÈS n'a pas de bascule : l'envoyer est le geste même de « Donner l'accès ». Les deux
 * autres en ont une — le compte rendu d'envoi à l'organisateur, et l'absence signalée reçue par vous.
 */
export function OrganisateurNotificationBlocks({ valeurs, onChange }: {
    valeurs: OrganisateurNotifications;
    onChange: (patch: Partial<OrganisateurNotifications>) => void;
}) {
    return (
        <GroupeNotifications titre="Portail organisateur" icone={<Link2 size={12} />}>
            <BlocEmail
                titre="Accès de l'organisateur (lien + code)"
                description="Part à la création de l'accès, au renvoi du lien et à chaque nouveau code (valable jusqu'à trois jours après l'événement)."
                sujet={valeurs.organisateur_acces_subject} setSujet={(v) => onChange({ organisateur_acces_subject: v })}
                corps={valeurs.organisateur_acces_body} setCorps={(v) => onChange({ organisateur_acces_body: v })}
                jetons={JETONS_ACCES} />
            <BlocEmail
                titre="Compte rendu à l'organisateur après vos envois"
                description="Part si vous cochez « Prévenir aussi l'organisateur » : il reçoit le nombre de convives silencieux, jamais leurs noms."
                bascule={{ checked: valeurs.notify_organisateur_envoi, onChange: (v) => onChange({ notify_organisateur_envoi: v }) }}
                sujet={valeurs.organisateur_envoi_subject} setSujet={(v) => onChange({ organisateur_envoi_subject: v })}
                corps={valeurs.organisateur_envoi_body} setCorps={(v) => onChange({ organisateur_envoi_body: v })}
                jetons={JETONS_ENVOI} />
            <BlocEmail
                titre="Absence signalée par l'organisateur (pour vous)"
                description="Un convive qui a déjà choisi son plat ne peut pas être retiré par l'organisateur : il vous signale l'absence."
                bascule={{ checked: valeurs.notify_organisateur_absence, onChange: (v) => onChange({ notify_organisateur_absence: v }) }}
                sujet={valeurs.organisateur_absence_subject} setSujet={(v) => onChange({ organisateur_absence_subject: v })}
                corps={valeurs.organisateur_absence_body} setCorps={(v) => onChange({ organisateur_absence_body: v })}
                jetons={JETONS_ABSENCE} />
        </GroupeNotifications>
    );
}
